import type { CSSProperties } from 'react'
import type { Match, ScorecardHole } from '../api/public'
import { provenanceMark, statusShort, statusWords } from './holeMarks'

/**
 * The pieces a round is read through, wherever it is read.
 *
 * # Why these are not inside a page
 *
 * They were inside `Event.tsx`, and `statusShort` was ALSO inside
 * `Scoreboard.tsx` -- the same six lines, written twice, because two pages
 * needed to turn "2UP A" into a name. One copy is the point: a hole grid that
 * disagreed with itself between two views of the same match is the class of
 * defect this whole consolidation exists to remove.
 *
 * Every value rendered here is computed by the server's match-play engine.
 * Deciding a hole in the browser would be a second implementation of a scoring
 * rule, and the two would drift on exactly the cases that are hard -- a half, a
 * pending hole, a match already settled.
 */

/**
 * The match hole by hole (FR-029).
 *
 * # Net, and only where both players finished
 *
 * A hole goes to the lower NET, which is the whole point of playing off
 * handicaps — the gross winner and the hole winner are routinely different
 * people. A hole only one of them completed is `pending` and awarded to nobody
 * (FR-035c, SC-015): the other may still hole out for a half.
 *
 * # The running status is what a player actually reads
 *
 * Who won hole 11 matters far less than where the match stood after it, which
 * is why the status is on the row rather than being left to be counted up.
 *
 * **Every value here is computed by the server's match-play engine.** Deciding
 * a hole in the browser would be a second implementation of a scoring rule, and
 * the two would drift on exactly the cases that are hard — a half, a pending
 * hole, a match already settled.
 */
export function MatchHoles({
  match,
  name,
}: {
  match: Match
  name: (id?: string | null) => string
}) {
  if (match.holes.length === 0) return null
  const a = name(match.player_a_id)
  const b = name(match.player_b_id)
  // **Uncontested has THREE causes and they read differently.** The match is
  // already decided and this hole scored nothing (FR-030, FR-035h); a player
  // has yet to hole out, so it may still be halved (FR-035c); or nobody has
  // reached it at all. The engine marks all three `pending`, which is why
  // `contested` is on the wire -- a client deriving it from the nets gets the
  // settled case wrong, because after a 4&2 the last two holes have no scores
  // and look exactly like holes nobody has played.
  const won = (side: 'a' | 'b', h: (typeof match.holes)[number]) => {
    if (h.contested) {
      return h.outcome === side ? 'hole-won' : h.outcome === 'halved' ? 'hole-halved' : 'hole-lost'
    }
    if (match.settled) return 'hole-dead'
    return h.a_net !== null || h.b_net !== null ? 'hole-pending' : 'hole-unplayed'
  }

  return (
    <div className="scroll-x">
      <table
        className="table match-holes"
        data-testid={`match-holes-${match.id}`}
        style={{ '--hole-columns': match.holes.length } as CSSProperties}
      >
        <thead>
          <tr>
            <th scope="col">Hole</th>
            {match.holes.map((h) => (
              <th key={h.hole} scope="col">
                {h.hole}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{a}</th>
            {match.holes.map((h) => (
              <td key={h.hole} className={won('a', h)} data-testid={`hole-${match.id}-${h.hole}-a`}>
                {h.a_net ?? '–'}
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row">{b}</th>
            {match.holes.map((h) => (
              <td key={h.hole} className={won('b', h)} data-testid={`hole-${match.id}-${h.hole}-b`}>
                {h.b_net ?? '–'}
              </td>
            ))}
          </tr>
          <tr className="match-status-row">
            <th scope="row">After</th>
            {match.holes.map((h) => (
              <td
                key={h.hole}
                data-testid={`after-${match.id}-${h.hole}`}
                title={
                  h.contested
                    ? statusWords(h.status_after, a, b)
                    : match.settled
                      ? 'the match was already decided; this hole scored nothing'
                      : h.a_net !== null || h.b_net !== null
                        ? 'waiting on a player to finish this hole'
                        : 'nobody has reached this hole'
                }
              >
                {h.contested
                  ? statusShort(h.status_after, a, b)
                  : match.settled
                    ? 'over'
                    : h.a_net !== null || h.b_net !== null
                      ? 'pending'
                      : '·'}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/** The visible mark on a score that did not simply arrive from a producer. */
export function ProvenanceMark({ hole }: { hole: ScorecardHole }) {
  const mark = provenanceMark[hole.provenance]
  if (!mark && !hole.flagged && !hole.replayed) return null
  return (
    <span className="mark-group">
      {mark && (
        <abbr className={`mark mark-${hole.provenance}`} title={`This score was ${mark}`}>
          {mark === 'typed' ? '✎' : '✔'}
        </abbr>
      )}
      {hole.replayed && (
        <abbr className="mark mark-replayed" title="This hole was played more than once">
          ↺
        </abbr>
      )}
      {hole.flagged && (
        <abbr className="mark mark-flagged" title={hole.flag_reason ?? 'Flagged for review'}>
          !
        </abbr>
      )}
    </span>
  )
}

/**
 * A replayed hole shows BOTH scores (FR-017c-1, SC-024).
 *
 * The most recent counts and the superseded one stays inspectable. Dropping it
 * would leave a card that quietly disagrees with what anyone watching saw.
 */
export function Replays({ holes }: { holes: ScorecardHole[] }) {
  const replayed = holes.filter((h) => h.replayed && h.superseded?.length)
  if (replayed.length === 0) return null
  return (
    <div className="replays" data-testid="replays">
      <h5>Replayed holes</h5>
      <ul>
        {replayed.map((h) => (
          <li key={h.hole}>
            Hole {h.hole}: <strong>{h.gross}</strong> counts.
            {h.superseded?.map((old, i) => (
              <span key={i} className="superseded">
                {' '}
                Earlier: <s>{old.gross}</s> ({old.provenance}).
              </span>
            ))}
          </li>
        ))}
      </ul>
    </div>
  )
}
